import {
  afterNextRender,
  Component,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HeaderMenuButtonComponent } from './header-menu-button.component';
import { NgOptimizedImage } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { HeaderService } from '../header.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { CurrentCustomClaimsService } from '@padspin/ui-rbac';
import { VoicePanelComponent } from '@padspin/ui-voice';
import { HeaderExtraButtonsComponent } from './header-extra-buttons.component';

@Component({
  selector: 'padspin-header',
  imports: [
    HeaderMenuButtonComponent,
    NgOptimizedImage,
    RouterLink,
    RouterLinkActive,
    HeaderExtraButtonsComponent,
    VoicePanelComponent,
  ],
  styles: `
    :host {
      display: block;
      position: sticky;
      top: 0;
      z-index: 1000;
    }
    :host(.overlay) {
      margin-bottom: calc(-1 * var(--header-height));
    }
    .notch {
      height: env(safe-area-inset-top);
    }
  `,
  template: `
    @if (!isHidden()) {
      <header
        class="w-full border-b border-surface bg-surface-50/80 dark:bg-surface-950/80 backdrop-blur-xl"
      >
        <div class="notch"></div>
        <div
          class="mx-auto flex h-[--header-height] max-w-7xl flex-row items-center justify-between gap-2 px-4 sm:px-6 lg:px-8"
        >
          <a routerLink="/" class="grow-0">
            <img
              width="153"
              height="45"
              [ngSrc]="logo"
              alt="Padspin logo"
              priority="1"
            />
          </a>
          @if (pageMenuItems().length) {
            <nav class="hidden items-center gap-8 lg:flex">
              @for (item of pageMenuItems(); track item.label) {
                <a
                  [routerLink]="item.routerLink"
                  routerLinkActive="text-color"
                  class="whitespace-nowrap text-sm font-medium text-muted-color transition-colors hover:text-color"
                  >{{ item.label }}</a
                >
              }
            </nav>
          }
          <div class="flex flex-row items-center gap-2">
            <padspin-header-extra-buttons />
            @defer (when loadVoicePanel()) {
              <padspin-voice-panel
                [btnSeverity]="voiceBtnSeverity"
                [btnOutlined]="voiceBtnOutlined"
                [btnText]="voiceBtnText"
              />
            }
            @defer (on viewport) {
              <padspin-header-menu-button />
            } @placeholder {
              <span>Menu</span>
            }
          </div>
        </div>
      </header>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.overlay]': 'bgColor() === "transparent"',
  },
})
export class HeaderComponent {
  private readonly headerService = inject(HeaderService);
  private readonly claimsSvc = inject(CurrentCustomClaimsService);

  /** Page-published nav items, shown inline on desktop (lg+). */
  protected readonly pageMenuItems = this.headerService.getMenuItems();

  private readonly claims = this.claimsSvc.currentCustomClaims();
  private readonly isVoiceAgent = computed(() => {
    const c = this.claims();
    return c?.admin === true || c?.account_manager === true;
  });

  /**
   * Twilio Voice is never a priority: it opens a WebSocket, registers a Device,
   * and heartbeats. Only load it for agents, and only once the browser has gone
   * idle after the initial render so it never competes with first paint.
   */
  private readonly settled = signal(false);
  protected readonly loadVoicePanel = computed(
    () => this.isVoiceAgent() && this.settled(),
  );

  constructor() {
    afterNextRender(() => {
      const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void) => void;
      };
      const whenIdle = w.requestIdleCallback ?? ((cb: () => void) => cb());
      whenIdle(() => setTimeout(() => this.settled.set(true), 2500));
    });
  }

  protected bgColor = toSignal(this.headerService.getBackground$());
  // The header is always the frosted marketing bar (light in light mode, dark
  // in dark mode), so logo + buttons use one fixed treatment instead of the
  // legacy bgColor variants that rendered white-on-blue (invisible here).
  protected readonly voiceBtnSeverity = 'secondary' as const;
  protected readonly voiceBtnOutlined = true;
  protected readonly voiceBtnText = false;
  protected readonly logo = '/assets/web/padspin-logo-green-blue-wordmark.svg';
  protected sticky = toSignal(this.headerService.getSticky$());
  protected isHidden = this.headerService.getHidden();
}
